import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";

import { createCompatEffect } from "@/lib/solid/effect";
import type { ButtonDOMProps } from "@/lib/solid/jsx-types";
import { omitProps } from "@/lib/solid/props";
import { setRefValue } from "@/lib/solid/ref";
import type { RefTarget } from "@/lib/solid/ref";
import { cn } from "@/lib/utils";

export const buttonVariants = cva(
  "group/button focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 inline-flex shrink-0 items-center justify-center rounded-2xl border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:ring-3 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:ring-3 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        default:
          "h-8 gap-1.5 px-3 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5",
        icon: "size-8",
        "icon-lg": "size-9",
        "icon-sm": "size-7",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        lg: "h-9 gap-1.5 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        sm: "h-7 gap-1 px-3 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 px-2.5 text-xs has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3",
      },
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        link: "text-primary underline-offset-4 hover:underline",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-input/30 dark:bg-transparent",
        secondary:
          "bg-secondary text-secondary-foreground aria-expanded:bg-secondary aria-expanded:text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)]",
      },
    },
  }
);

export type ButtonProps = Omit<
  ButtonDOMProps,
  "children" | "class" | "className" | "onClick" | "ref" | "tabIndex" | "type"
> &
  VariantProps<typeof buttonVariants> & {
    class?: string;
    className?: string;
    children?: ButtonDOMProps["children"];
    ref?: RefTarget<HTMLButtonElement>;
    type?: "button" | "submit" | "reset";
    onClick?: (event: MouseEvent) => void;
    tabIndex?: number;
  };

const OWN_PROPS = [
  "class",
  "className",
  "children",
  "ref",
  "size",
  "tabIndex",
  "type",
  "variant",
] as const;

export const Button = (props: ButtonProps) => {
  const rest = omitProps(props, OWN_PROPS);
  let element: HTMLButtonElement | undefined;

  createCompatEffect(
    () => props.tabIndex ?? 0,
    (tabIndex) => {
      if (element) {
        element.tabIndex = tabIndex;
      }
    }
  );

  return (
    <button
      class={cn(
        buttonVariants({
          className: cn(props.class, props.className),
          size: props.size ?? "default",
          variant: props.variant ?? "default",
        })
      )}
      data-slot="button"
      ref={(node) => {
        element = node;
        node.tabIndex = props.tabIndex ?? 0;
        setRefValue(props.ref, node);
      }}
      type={props.type ?? "button"}
      {...rest}
    >
      {props.children}
    </button>
  );
};
